import React, { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
  TimeScale,
} from "chart.js";
import "chartjs-adapter-date-fns";
import ItemService from "../../../../services/supplier_appCommonService";

ChartJS.register(
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler
);

const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December"
];

const TIME_RANGES = [
  { label: "12 Hours", value: 12 },
  { label: "24 Hours", value: 24 },
  { label: "Monthly", value: "monthly" },
];

const fetchAllPurchaseReq = async (api) => {
  let page = 1;
  const per_page = 200;
  let all = [];

  while (true) {
    const res = await api({ page, per_page, searchTerm: "" });
    all.push(...(res?.data || []));
    if (all.length >= (res?.total || 0)) break;
    page++;
  }
  return all;
};

const PurchaseReqMonthlyChart = () => {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth());
  const [year, setYear] = useState(now.getFullYear());
  const [timeRange, setTimeRange] = useState("monthly");
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line
  }, [month, year, timeRange]);

  const loadData = async () => {
    try {
      setLoading(true);

      const allRecords = await fetchAllPurchaseReq(
        ItemService.getPurchaseRequisition
      );

      if (timeRange === 12 || timeRange === 24) {
        loadHourlyData(allRecords, timeRange);
      } else {
        loadMonthlyData(allRecords);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadHourlyData = (allRecords, hours) => {
    const now = new Date();
    const cutoffTime = new Date(now.getTime() - hours * 60 * 60 * 1000);

    const filteredRecords = allRecords.filter((item) => {
      if (!item?.createdAt) return false;
      const createdDate = new Date(item.createdAt);
      return createdDate >= cutoffTime && createdDate <= now;
    });

    const hourMap = {};

    filteredRecords.forEach((item) => {
      const d = new Date(item.createdAt);
      const hourKey = new Date(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), 0, 0, 0);
      const timeKey = hourKey.getTime();
      
      if (!hourMap[timeKey]) {
        hourMap[timeKey] = 0;
      }
      hourMap[timeKey] += Number(item.purchasingReqDocumentValue || 0);
    });

    const chartPoints = Object.keys(hourMap)
      .map((timestamp) => ({
        x: Number(timestamp),
        y: hourMap[timestamp],
      }))
      .sort((a, b) => a.x - b.x);

    console.log("Chart points:", chartPoints.length);
    setPoints(chartPoints);
  };

  const loadMonthlyData = (allRecords) => {
    const totalDays = new Date(year, month + 1, 0).getDate();
    const dayValueMap = {};

    for (let d = 1; d <= totalDays; d++) {
      dayValueMap[d] = 0;
    }

    allRecords.forEach((item) => {
      if (!item?.createdAt) return;
      const d = new Date(item.createdAt);
      if (d.getMonth() === month && d.getFullYear() === year) {
        const day = d.getDate();
        dayValueMap[day] += Number(item.purchasingReqDocumentValue || 0);
      }
    });

    const chartPoints = [];
    for (let day = 1; day <= totalDays; day++) {
      chartPoints.push({
        x: day,
        y: dayValueMap[day]
      });
    }
    
    setPoints(chartPoints);
  };

  const isHourlyView = timeRange === 12 || timeRange === 24;

  const getPointColors = () => {
    if (!isHourlyView || points.length === 0) {
      return "#2563eb";
    }

    return points.map((point, index) => {
      if (index === 0) return "#6366f1";
      
      const prevValue = points[index - 1].y;
      const currentValue = point.y;
      
      if (currentValue > prevValue) {
        return "#10b981";
      } else if (currentValue < prevValue) {
        return "#ef4444";
      } else {
        return "#6b7280";
      }
    });
  };

  const chartData = {
    datasets: [
      {
        label: "Purchase Requisition Value",
        data: points,
        parsing: false,

        borderColor: "#2563eb",
        backgroundColor: "rgba(37,99,235,0.15)",
        fill: true,
        tension: 0.1,

        pointRadius: 7,
        pointHoverRadius: 10,
        pointHitRadius: 30,
        pointBackgroundColor: getPointColors(),
        pointBorderColor: "#ffffff",
        pointBorderWidth: 3,

        segment: {
          borderColor: (ctx) => {
            if (!isHourlyView) return "#2563eb";
            
            const idx = ctx.p0DataIndex;
            if (idx >= points.length - 1) return "#2563eb";
            
            const currentValue = points[idx + 1]?.y;
            const prevValue = points[idx]?.y;
            
            if (currentValue > prevValue) {
              return "#10b981";
            } else if (currentValue < prevValue) {
              return "#ef4444";
            }
            return "#6b7280";
          },
          borderWidth: 3,
        },

        clip: false,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,

    // **CRITICAL**: Ensure interaction is enabled
    interaction: {
      mode: "nearest",
      intersect: false,
      axis: "x",
    },

    // **CRITICAL**: Disable animations temporarily to test tooltips
    animation: false,

    plugins: {
      legend: { 
        display: false,
      },
      tooltip: {
        enabled: true, // **CRITICAL**: Explicitly enable tooltips
        mode: "nearest",
        intersect: false,
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        titleColor: "#fff",
        bodyColor: "#fff",
        borderColor: "#666",
        borderWidth: 1,
        padding: 12,
        displayColors: false,
        
        callbacks: {
          title: (tooltipItems) => {
            console.log("Tooltip triggered!", tooltipItems); // Debug log
            
            if (isHourlyView) {
              const date = new Date(tooltipItems[0].parsed.x);
              return date.toLocaleString("en-IN", {
                weekday: "short",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
              });
            }
            
            const index = tooltipItems[0].dataIndex;
            const day = index + 1;
            return `${MONTHS[month]} ${day}, ${year}`;
          },
          label: (ctx) => {
            const value = ctx.parsed.y;
            let formattedValue;
            
            if (value >= 10000000) {
              formattedValue = `₹${(value / 10000000).toFixed(2)} Cr`;
            } else if (value >= 100000) {
              formattedValue = `₹${(value / 100000).toFixed(2)} L`;
            } else if (value >= 1000) {
              formattedValue = `₹${(value / 1000).toFixed(2)}k`;
            } else {
              formattedValue = `₹${value.toLocaleString("en-IN")}`;
            }
            
            return `Total PR Value: ${formattedValue}`;
          },
          afterLabel: (ctx) => {
            if (ctx.dataIndex > 0 && points.length > 1) {
              const currentValue = ctx.parsed.y;
              const previousValue = points[ctx.dataIndex - 1]?.y || 0;
              const diff = currentValue - previousValue;
              
              if (diff > 0 && previousValue > 0) {
                const percentChange = ((diff / previousValue) * 100).toFixed(1);
                return `Increased by ₹${diff.toLocaleString("en-IN")} (+${percentChange}%)`;
              } else if (diff < 0 && previousValue > 0) {
                const percentChange = ((Math.abs(diff) / previousValue) * 100).toFixed(1);
                return `Decreased by ₹${Math.abs(diff).toLocaleString("en-IN")} (-${percentChange}%)`;
              }
            }
            return null;
          }
        },
      },
    },

    scales: {
      x: isHourlyView ? {
        type: "time",
        time: {
          unit: "hour",
          displayFormats: {
            hour: "ha",
          },
        },
        ticks: {
          font: { size: 11 },
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 12,
        },
        grid: { 
          display: true,
          color: "rgba(0,0,0,0.05)",
        },
      } : {
        type: "linear",
        min: 1,
        max: new Date(year, month + 1, 0).getDate(),
        ticks: {
          stepSize: 1,
          font: { size: 10 },
        },
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        ticks: {
          callback: (v) => {
            if (v >= 10000000) return `₹${(v / 10000000).toFixed(1)}Cr`;
            if (v >= 100000) return `₹${(v / 100000).toFixed(0)}L`;
            if (v >= 1000) return `₹${(v / 1000).toFixed(0)}k`;
            return `₹${v}`;
          },
        },
        grid: { color: "#e5e7eb" },
      },
    },
  };

  const getChartTitle = () => {
    if (timeRange === 12) return "Last 12 Hours";
    if (timeRange === 24) return "Last 24 Hours";
    return `${MONTHS[month]} ${year}`;
  };

  return (
    <div className="card p-3" style={{ borderRadius: "14px", overflow: "visible" }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h6 className="fw-bold mb-1">
            Purchase Requisition – {getChartTitle()}
          </h6>
          {isHourlyView && points.length > 0 && (
            <div className="d-flex gap-3" style={{ fontSize: "11px", color: "#6b7280" }}>
              <span><span style={{ color: "#10b981" }}>●</span> Increase</span>
              <span><span style={{ color: "#ef4444" }}>●</span> Decrease</span>
              <span><span style={{ color: "#6b7280" }}>●</span> No Change</span>
            </div>
          )}
        </div>

        <div className="d-flex gap-2">
          <select
            className="form-select form-select-sm"
            value={timeRange}
            onChange={(e) => {
              const value = e.target.value;
              setTimeRange(value === "monthly" ? "monthly" : Number(value));
            }}
            style={{ minWidth: "120px" }}
          >
            {TIME_RANGES.map((range) => (
              <option key={range.value} value={range.value}>
                {range.label}
              </option>
            ))}
          </select>

          {!isHourlyView && (
            <>
              <select
                className="form-select form-select-sm"
                value={month}
                onChange={(e) => setMonth(+e.target.value)}
              >
                {MONTHS.map((m, i) => (
                  <option key={i} value={i}>{m}</option>
                ))}
              </select>

              <select
                className="form-select form-select-sm"
                value={year}
                onChange={(e) => setYear(+e.target.value)}
              >
                {[year - 2, year - 1, year, year + 1].map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-4">
          <div className="spinner-border spinner-border-sm text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : points.length === 0 ? (
        <div className="text-center py-4 text-muted">
          <p className="mb-0">No purchase requisitions in selected range</p>
        </div>
      ) : (
        <div style={{ height: 280, position: "relative", zIndex: 1 }}>
          <Line data={chartData} options={chartOptions} />
          
          {isHourlyView && points.length > 0 && (
            <div className="mt-3 d-flex gap-4" style={{ fontSize: "12px" }}>
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted">📊 Hours:</span>
                <span className="fw-semibold">{points.length}</span>
              </div>
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted">💰 Total:</span>
                <span className="fw-semibold">
                  ₹{points.reduce((sum, p) => sum + p.y, 0).toLocaleString("en-IN")}
                </span>
              </div>
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted">📈 Peak:</span>
                <span className="fw-semibold">
                  ₹{Math.max(...points.map(p => p.y)).toLocaleString("en-IN")}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PurchaseReqMonthlyChart;
